import { defineComponent, nextTick, onBeforeUnmount, onMounted, ref, toRefs } from 'vue';
import { track } from '../tracking/trackingLayer.js';
import EventBus from '../../scripts/lib/EventBus.js';
import { dispatchVspOpen } from '../vsp-modal/vsp-event.js';

const templateElement = document.getElementById('add-to-cart-template');

export default defineComponent({
  template: templateElement ? templateElement.innerHTML : '',

  props: {
    syan: {
      type: Number,
      required: true,
    },
    article: {
      type: Object,
      required: true,
    },
    articleId: {
      type: String,
      required: true,
    },
    isInitialArticle: {
      type: Boolean,
      required: true,
    },
    dimension: {
      type: String,
      required: true,
    },
    isVspEnabled: {
      type: Boolean,
      required: false,
    },
  },

  setup(props) {
    const { isInitialArticle, articleId } = toRefs(props);
    const addToCartButton = ref<HTMLElement | null>(null);
    const hideVspButtonInFavorOfVspSlider = ref(false);

    function onSimilarInSizeShown() {
      hideVspButtonInFavorOfVspSlider.value = true;
    }

    function onSimilarInSizeHidden() {
      hideVspButtonInFavorOfVspSlider.value = false;
    }

    function addToCartAfterSizeSelection() {
      nextTick(() => {
        if (props.article.outOfStock) {
          return;
        }
        emitCartContent();
      });
      EventBus.off('ents:vue:layer:hide', addToCartAfterSizeSelection);
    }

    function openSizeLayerForAddToCart(overrideEventContext: string | undefined = undefined) {
      EventBus.emit('ents:vue:dimension:select', {
        dimension: props.dimension,
        add2CartAfterSizeSelection: true,
        overrideEventContext,
      });
      EventBus.on('ents:vue:layer:hide', addToCartAfterSizeSelection);
      sendTrackingInfo(['click', 'cart_sizelayer']);
    }

    function addToCart() {
      if (isInitialArticle.value) {
        openSizeLayerForAddToCart();
      } else if (articleId.value) {
        emitCartContent();
      }
    }

    function getA2CPayload() {
      const payload: { detail: AddToCartPayload } = {
        detail: {
          artikel: getSalesId(),
          menge: 1,
        },
      };
      const salesInformationId = getSalesInformationId();
      if (salesInformationId) {
        payload.detail.vertriebsinfoId = salesInformationId;
      }
      return payload;
    }

    // this method is used for mocking in unit tests
    function getSalesId() {
      return props.article.salesId;
    }

    // this method is used for mocking in unit tests
    function getSalesInformationId() {
      return props.article.salesInformationId;
    }

    function emitCartContent() {
      // see: https://brix.dev.breuni.de/kaufen/sandbox/ssi.html
      const payload = getA2CPayload();
      const customKaufCartAddEvent = new CustomEvent('kaufen:cart:add', payload);
      document.dispatchEvent(customKaufCartAddEvent);
      console.info('Article was handed over to cart:', payload);
      addToCartButton.value?.setAttribute('data-test-ents-add-to-cart-payload-emitted', JSON.stringify(payload));
      window.performance.mark('ents.pds.add-to-cart.payload-emitted');
    }

    function sendTrackingInfo(eventValues: ('click' | 'cart_sizelayer')[]) {
      track({
        event_level1: 'ents',
        event_level2: null,
        event_level3: null,
        event_level4: null,
        event_level5: null,
        event: 'event.generic',
        event_values: eventValues,
      });
    }

    function a2cClick() {
      if (props.article.outOfStock) {
        handleOutOfStockVariant();
      } else {
        addToCart();
      }
    }

    function vspClick() {
      const recoVspEnabled = document.querySelector('#ents-layer__vsp') != null;
      const payload = dispatchVspOpen(recoVspEnabled, {
        syan: props.syan,
        colorId: props.article.colors?.active?.colorId || undefined,
        listName: 'pds_cta',
      });
      addToCartButton.value?.setAttribute('data-test-ents-vsp-cta-payload-emitted', JSON.stringify(payload));
    }

    function handleOutOfStockVariant() {
      if (isInitialArticle.value) {
        EventBus.emit('ents:vue:dimension:select', { dimension: props.dimension });
      } else {
        EventBus.emit('ents:out-of-stock-layer:show', getSalesInformationId());
      }
    }

    async function addToCartFromEvent(e: CustomEvent<AddToCartEventDetail>) {
      await addToCartFromEventWithArticleId(e.detail.itemId ?? e.detail.articleId, e.detail.eventContext);
    }

    async function addToCartFromEventWithArticleId(articleId?: string, eventContext?: string) {
      if (!articleId && props.dimension === 'sizes') {
        openSizeLayerForAddToCart(eventContext);
        return;
      }

      EventBus.emit('ents:vue:article:changed', articleId);
      // wait a tick so the reactive articleId update from article:changed is flushed before reading it
      await nextTick();
      emitCartContent();
    }

    onMounted(() => {
      document.addEventListener('ents:article:addtocart', addToCartFromEvent); // addtocart dispatched by BRTN
      document.addEventListener('brtn:similar-in-size:shown', onSimilarInSizeShown); // dispatched by BRTN
      document.addEventListener('brtn:similar-in-size:hidden', onSimilarInSizeHidden); // dispatched by BRTN
    });

    onBeforeUnmount(() => {
      document.removeEventListener('ents:article:addtocart', addToCartFromEvent);
      document.removeEventListener('brtn:similar-in-size:shown', onSimilarInSizeShown);
      document.removeEventListener('brtn:similar-in-size:hidden', onSimilarInSizeHidden);
    });

    return {
      a2cClick,
      vspClick,
      addToCart: addToCartButton,
      hideVspButtonInFavorOfVspSlider,
    };
  },
});
